import { supabase } from '../lib/supabase/client';
import { ContactSubmission } from '../types/models';
import { Database } from '../types/database.types';

type ContactRow = Database['public']['Tables']['contact_submissions']['Row'];
type ContactInsert = Database['public']['Tables']['contact_submissions']['Insert'];
type ContactUpdate = Database['public']['Tables']['contact_submissions']['Update'];

function toContactSubmission(row: ContactRow): ContactSubmission {
  return {
    id: row.id,
    fullName: row.full_name,
    email: row.email,
    phone: row.phone ?? undefined,
    message: row.message,
    status: row.status,
    adminNotes: row.admin_notes ?? undefined,
    assignedTo: row.assigned_to ?? undefined,
    submittedAt: row.submitted_at,
    lastUpdated: row.last_updated,
    ipAddress: row.ip_address ?? undefined,
    userAgent: row.user_agent ?? undefined,
  };
}

export const contactService = {
  async getAll(): Promise<{ data: ContactSubmission[] | null; error: Error | null }> {
    const { data, error } = await supabase
      .from('contact_submissions')
      .select('*')
      .order('submitted_at', { ascending: false });

    if (error) return { data: null, error };
    return { data: data?.map(toContactSubmission) ?? [], error: null };
  },

  async getById(id: string): Promise<{ data: ContactSubmission | null; error: Error | null }> {
    const { data, error } = await supabase
      .from('contact_submissions')
      .select('*')
      .eq('id', id)
      .single();

    if (error) return { data: null, error };
    return { data: data ? toContactSubmission(data) : null, error: null };
  },

  async updateStatus(
    id: string,
    status: ContactSubmission['status']
  ): Promise<{ data: ContactSubmission | null; error: Error | null }> {
    const updateData: ContactUpdate = { status };
    const { data, error } = await supabase
      .from('contact_submissions')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) return { data: null, error };
    return { data: data ? toContactSubmission(data) : null, error: null };
  },

  async updateNotes(
    id: string,
    adminNotes: string
  ): Promise<{ data: ContactSubmission | null; error: Error | null }> {
    const updateData: ContactUpdate = { admin_notes: adminNotes };
    const { data, error } = await supabase
      .from('contact_submissions')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) return { data: null, error };
    return { data: data ? toContactSubmission(data) : null, error: null };
  },

  async delete(id: string): Promise<{ error: Error | null }> {
    const { error } = await supabase
      .from('contact_submissions')
      .delete()
      .eq('id', id);

    return { error };
  },

  async create(submission: Omit<ContactSubmission, 'id' | 'submittedAt' | 'lastUpdated'>): Promise<{
    data: ContactSubmission | null;
    error: Error | null;
  }> {
    const insertData: ContactInsert = {
      full_name: submission.fullName,
      email: submission.email,
      phone: submission.phone,
      message: submission.message,
      status: submission.status ?? 'new',
      admin_notes: submission.adminNotes,
      assigned_to: submission.assignedTo,
      ip_address: submission.ipAddress,
      user_agent: submission.userAgent,
    };

    const { data, error } = await supabase
      .from('contact_submissions')
      .insert(insertData)
      .select()
      .single();

    if (error) return { data: null, error };
    return { data: data ? toContactSubmission(data) : null, error: null };
  },
};